import {
  FILE_MAX_SIZE_IN_BYTES,
  FILE_REJECTED_TRANSLATIONS,
  FileErrorCode,
  MAX_ALLOWED_ATTACHMENTS
} from '@hconnect/common/consts'
import {BackendFile, spawnInfoToast} from '@hm/fundamental'
import {useCallback, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {COMMON_ATTACHMENTS_WIDGET_NAMESPACE} from '../consts'
import {type AllowedFileFormat, type FileUploadEvent, FileUploadEventType} from '../types'

import {type AttachmentFile, DragAndDropFiles} from './DragAndDropFiles'

const findDuplicateFiles = (attachments: AttachmentFile[]): AttachmentFile[] => {
  const processedNames = new Set<string>()
  return attachments.filter((attachment) => {
    if (processedNames.has(attachment.name)) {
      return true
    }
    processedNames.add(attachment.name)
    return false
  })
}

const removeDuplicateFiles = (attachments: AttachmentFile[]): AttachmentFile[] => {
  const processedNames = new Set<string>()
  return attachments.filter((attachment) => {
    if (processedNames.has(attachment.name)) {
      return false
    }
    processedNames.add(attachment.name)
    return true
  })
}

const MEGA_BYTE_IN_BYTES = 1024 * 1024
const FILE_MAX_SIZE_IN_MB = FILE_MAX_SIZE_IN_BYTES / MEGA_BYTE_IN_BYTES

const ALLOWED_DOCUMENT_FORMATS: AllowedFileFormat[] = ['.pdf']
const ALLOWED_IMAGE_FORMATS: AllowedFileFormat[] = ['.png', '.jpg', '.jpeg']
const ALLOWED_WORKSHEET_FORMATS: AllowedFileFormat[] = ['.xlsx', '.xls']
const ALLOWED_VIDEO_FORMATS: AllowedFileFormat[] = ['.mp4', '.3gp', '.mov']

const allowedFormats = [
  ...ALLOWED_DOCUMENT_FORMATS,
  ...ALLOWED_IMAGE_FORMATS,
  ...ALLOWED_WORKSHEET_FORMATS,
  ...ALLOWED_VIDEO_FORMATS
]

type AttachmentsDropzoneProps = {
  value: AttachmentFile[]
  onChange: (files: AttachmentFile[]) => void
  hint?: string
}

export const AttachmentsDropzone = ({
  value: attachments,
  onChange,
  hint
}: AttachmentsDropzoneProps) => {
  const {t} = useTranslation(COMMON_ATTACHMENTS_WIDGET_NAMESPACE)
  const [lastChangedAt, setLastChangedAt] = useState(Date.now())

  const notifyFileUploadEvent = useCallback(
    (event: FileUploadEvent) => {
      switch (event.type) {
        case FileUploadEventType.FILE_DUPLICATED:
          return spawnInfoToast(`${t('attachmentUpload.message.duplicateFiles')}${event.fileName}`)
        case FileUploadEventType.FILE_SIZE_TOO_LARGE:
          return spawnInfoToast(
            `${t(FILE_REJECTED_TRANSLATIONS[FileErrorCode.TOO_LARGE])} ${event.fileName}`
          )
        case FileUploadEventType.FILE_TYPE_INVALID:
          return spawnInfoToast(
            `${t(FILE_REJECTED_TRANSLATIONS[FileErrorCode.INVALID_TYPE])} ${event.fileName}`
          )
        case FileUploadEventType.FILE_LIMIT_REACHED:
          return spawnInfoToast(
            `${t('attachmentUpload.message.fileLimitReached')}${event.fileName}`
          )
      }
    },
    [t]
  )

  const validateAttachment = useCallback(
    (attachment: File): boolean => {
      if (!(attachment instanceof File)) {
        return true
      }

      if (!allowedFormats.some((format) => attachment.name.toLowerCase().includes(format))) {
        notifyFileUploadEvent({
          type: FileUploadEventType.FILE_TYPE_INVALID,
          fileName: attachment.name
        })
        return false
      }

      if (attachment.size > FILE_MAX_SIZE_IN_BYTES) {
        notifyFileUploadEvent({
          type: FileUploadEventType.FILE_SIZE_TOO_LARGE,
          fileName: attachment.name
        })
        return false
      }

      return true
    },
    [notifyFileUploadEvent]
  )

  const handleAttachmentsChange = useCallback(
    (newAttachments: AttachmentFile[]) => {
      const fileAttachments = newAttachments.filter(
        (attachment): attachment is File => attachment instanceof File
      )
      const backendFileAttachments = newAttachments.filter(
        (attachment): attachment is BackendFile => !(attachment instanceof File)
      )

      const validatedFileAttachments = fileAttachments.filter((attachment) =>
        validateAttachment(attachment)
      )

      const allValidatedAttachments = [...validatedFileAttachments, ...backendFileAttachments]
      const validatedAndUniqueAttachments = removeDuplicateFiles(allValidatedAttachments)

      const duplicateFiles = findDuplicateFiles(allValidatedAttachments)
      if (duplicateFiles.length) {
        duplicateFiles.forEach((filtered) => {
          notifyFileUploadEvent({
            type: FileUploadEventType.FILE_DUPLICATED,
            fileName: filtered.name
          })
        })
      }

      const exceededFiles = validatedAndUniqueAttachments.slice(MAX_ALLOWED_ATTACHMENTS)
      if (exceededFiles.length) {
        exceededFiles.forEach((exceeded) => {
          notifyFileUploadEvent({
            type: FileUploadEventType.FILE_LIMIT_REACHED,
            fileName: exceeded.name
          })
        })
      }

      const updatedAttachments = validatedAndUniqueAttachments.slice(0, MAX_ALLOWED_ATTACHMENTS)

      onChange(updatedAttachments)
      setLastChangedAt(Date.now())
    },
    [validateAttachment, notifyFileUploadEvent, onChange]
  )

  const handleRemoveFile = useCallback(
    (fileId: string) => {
      const updatedAttachments = attachments.filter((attachment) => {
        const identifier = (attachment as BackendFile).id || attachment.name
        return identifier !== fileId
      })
      onChange(updatedAttachments)
      setLastChangedAt(Date.now())
    },
    [attachments, onChange]
  )

  return (
    <DragAndDropFiles
      onRemoveFile={handleRemoveFile}
      key={lastChangedAt}
      initialValue={attachments}
      onChange={handleAttachmentsChange}
      hint={
        hint ??
        t('attachmentUpload.label.fileRequirements', {
          maxFiles: MAX_ALLOWED_ATTACHMENTS,
          maxSizeInMB: FILE_MAX_SIZE_IN_MB,
          allowedFormats: allowedFormats.join(', ')
        })
      }
    />
  )
}
